import axios from 'axios';
import i18n from '../i18n';

import { getTenantSlug } from './tenant';
import { expireMesaSession, getMesaSessionToken } from './mesa';

/**
 * The single place that knows where the API is and who is calling it.
 *
 * This replaces `http://${window.location.hostname}:8000` repeated at 26 call
 * sites, each of which also re-read the auth token from localStorage and
 * rebuilt the same headers. One of them forgetting a header was a bug waiting
 * to happen; now there is nothing to forget.
 */

const AUTH_TOKEN_KEY = 'auth_token';
export const SESSION_EXPIRED_EVENT = 'bamidi:session-expired';
let inMemoryToken: string | null = null;

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode or blocked storage.
    return null;
  }
};

export const getAuthToken = (): string | null => inMemoryToken ?? readStorage(AUTH_TOKEN_KEY);

export const setAuthToken = (token: string): void => {
  inMemoryToken = token;
  try {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    // The interceptor still sends it for this page load.
  }
};

export const clearAuthToken = (): void => {
  inMemoryToken = null;
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // ignored
  }
};

export const resolveBaseUrl = (): string => {
  const configured = import.meta.env.VITE_API_URL;
  if (configured) return configured.replace(/\/$/, '');
  // Vite proxies API requests locally, so a single shared port also works
  // over HTTPS tunnels. Deployments can override this with VITE_API_URL.
  return window.location.origin;
};

export const api = axios.create({ baseURL: resolveBaseUrl() });

api.interceptors.response.use((response) => response, (error: unknown) => {
  if (axios.isAxiosError(error) && error.response?.status === 403
    && error.response.data?.code === 'table_session_expired') {
    const session = error.config?.headers?.['X-Table-Session'];
    if (typeof session === 'string') expireMesaSession(session);
  }
  if (axios.isAxiosError(error) && error.response?.status === 401
    && getAuthToken() && error.config?.headers?.Authorization === `Token ${getAuthToken()}`
    && error.config.url !== '/api/login/') {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
  return Promise.reject(error);
});

api.interceptors.request.use((config) => {
  if (/^\/api\/(?:pedidos|solicitacoes)(?:\/|$)/.test(config.url ?? '')) {
    const session = getMesaSessionToken();
    if (session && !config.headers['X-Table-Session']) config.headers['X-Table-Session'] = session;
  }
  config.headers['Accept-Language'] = i18n.language || 'pt';
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  // Which restaurant this request is for. Staff requests can fall back to the
  // user's membership server-side, but sending it is always correct.
  const tenant = getTenantSlug();
  if (tenant) {
    config.headers['X-Tenant'] = tenant;
  }
  return config;
});

/**
 * Called by staff screens when a request comes back unauthenticated, so the
 * redirect to the login page lives in one place rather than in every page's
 * error handler.
 */
export const isAuthError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  return error.response?.status === 401 || error.response?.status === 403;
};

export const errorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const firstMessage = (value: unknown): string | undefined => {
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) return value.map(firstMessage).find(Boolean);
      if (value && typeof value === 'object') return Object.values(value).map(firstMessage).find(Boolean);
      return undefined;
    };
    const data: unknown = error.response?.data;
    // Never expose HTML error pages from a proxy as a customer message.
    return data && typeof data === 'object' ? firstMessage(data) ?? fallback : fallback;
  }
  return fallback;
};
